import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * <aromely-giftcard-pdp> — the eGift Card page (draft theme). Picks a card value (a real variant),
 * optionally addresses the card to someone (Shopify's native recipient properties, so the store
 * emails the card to them on the chosen date), and adds the line through Theme.routes.cart_add_url.
 * Nothing is invented: amounts, prices and availability come from the product; recipient handling is
 * Shopify's own (properties[Recipient email|Recipient name|Message|Send on], __shopify_send_gift_card_to_recipient).
 */
class AromelyGiftcardPdp extends Component {
  #ac = new AbortController();
  #sticky = null;
  #stickyIO = null;
  #drawerMO = null;
  #amounts = [];
  #amount = null;
  mode = 'self';
  qty = 1;
  busy = false;

  connectedCallback() {
    super.connectedCallback();
    try { this.#amounts = JSON.parse(this.dataset.amounts || '[]'); } catch { this.#amounts = []; }
    const wanted = new URL(location.href).searchParams.get('variant');
    this.#amount = this.#amounts.find((a) => String(a.id) === wanted) || this.#amounts.find((a) => a.available) || this.#amounts[0] || null;
    this.#setDateBounds();
    for (const f of this.#fields()) f.addEventListener('input', () => this.#paintPreview(), { signal: this.#ac.signal });
    this.setupSticky(this.closest('[data-agc-root]'));
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac.abort();
    this.#stickyIO?.disconnect();
    this.#drawerMO?.disconnect();
    this.setStickyVisible(false);
  }

  money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  /** Elements that live outside this component (price head, live card) — not in this.refs. */
  #outer(name) {
    return this.closest('[data-agc-root]')?.querySelector(`[ref="${name}"]`) || null;
  }

  #fields() {
    const r = this.refs;
    return [r.recipientEmail, r.recipientName, r.recipientMessage, r.recipientSendOn].filter(Boolean);
  }

  /* ─── amount ─── */
  pickAmount(event) {
    const btn = event.target.closest('[data-amount-id]');
    if (!btn || btn.disabled) return;
    const next = this.#amounts.find((a) => String(a.id) === btn.dataset.amountId);
    if (!next) return;
    this.#amount = next;
    try {
      const url = new URL(location.href);
      url.searchParams.set('variant', String(next.id));
      history.replaceState(history.state, '', url);
    } catch { /* ignore */ }
    this.render();
  }

  /* ─── who it's for ─── */
  setMode(mode) {
    if (mode !== 'self' && mode !== 'recipient') return;
    if (this.mode === mode) return;
    this.mode = mode;
    this.error('');
    this.#clearFieldErrors();
    if (mode === 'self') {
      for (const f of this.#fields()) f.value = '';
    } else {
      this.qty = 1; // one card, one recipient
      this.#setDateBounds();
    }
    this.render();
    if (mode === 'recipient') this.refs.recipientEmail?.focus();
  }

  #setDateBounds() {
    const el = this.refs.recipientSendOn;
    if (!el) return;
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = new Date();
    const max = new Date();
    max.setDate(today.getDate() + 90); // Shopify accepts up to 90 days ahead
    el.min = fmt(today);
    el.max = fmt(max);
  }

  qtyUp() { if (this.mode === 'self' && this.qty < 10) { this.qty += 1; this.render(); } }
  qtyDown() { if (this.qty > 1) { this.qty -= 1; this.render(); } }

  /* ─── cart ─── */
  async fetchCart() {
    try {
      return await fetch(`${Theme.routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((x) => x.json());
    } catch { return null; }
  }

  #recipientProperties() {
    const r = this.refs;
    const props = {
      'Recipient email': (r.recipientEmail?.value || '').trim(),
      'Recipient name': (r.recipientName?.value || '').trim(),
      'Message': (r.recipientMessage?.value || '').trim(),
      '__shopify_send_gift_card_to_recipient': 'on',
      '__shopify_offset': String(new Date().getTimezoneOffset()),
    };
    const sendOn = (r.recipientSendOn?.value || '').trim();
    if (sendOn) props['Send on'] = sendOn;
    if (!props['Recipient name']) delete props['Recipient name'];
    if (!props['Message']) delete props['Message'];
    return props;
  }

  /** Field checks we can do before asking the store (the store validates again). */
  #validateRecipient() {
    const r = this.refs;
    this.#clearFieldErrors();
    let ok = true;
    const email = (r.recipientEmail?.value || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      this.#fieldError('email', email ? 'That email address doesn’t look right.' : 'Add the recipient’s email so we know where to send the card.');
      ok = false;
    }
    if ((r.recipientMessage?.value || '').length > 200) { this.#fieldError('message', 'Keep the message to 200 characters.'); ok = false; }
    const sendOn = r.recipientSendOn?.value;
    if (sendOn && (sendOn < r.recipientSendOn.min || sendOn > r.recipientSendOn.max)) {
      this.#fieldError('send_on', 'Pick a date between today and 90 days from now.');
      ok = false;
    }
    if (!ok) this.refs.recipientEmail?.closest('[data-agc-fields]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return ok;
  }

  async addToCart() {
    if (this.busy || !this.#amount || !this.#amount.available) return;
    if (this.mode === 'recipient' && !this.#validateRecipient()) return;
    this.busy = true;
    this.error('');
    this.render();
    const item = { id: Number(this.#amount.id), quantity: this.qty };
    if (this.mode === 'recipient') item.properties = this.#recipientProperties();
    const before = (await this.fetchCart())?.item_count;
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        this.#showStoreErrors(data);
        return;
      }
      const cart = await this.fetchCart();
      this.dispatchEvent(new CartAddEvent(data, this.id, {
        source: 'aromely-giftcard-pdp',
        productId: this.dataset.productId,
        variantId: String(item.id),
        itemCount: cart?.item_count,
      }));
    } catch (err) {
      console.error('[aromely-giftcard-pdp] add to cart failed', err);
      const cart = await this.fetchCart();
      if (cart && before !== undefined && cart.item_count !== before) {
        this.dispatchEvent(new CartAddEvent({ items: [] }, this.id, { source: 'aromely-giftcard-pdp', itemCount: cart.item_count }));
        this.error('We couldn’t confirm this with the store, but your cart did change — please check it before trying again.');
      } else {
        this.error('We couldn’t confirm this with the store — check your connection and try again. Nothing was added.');
      }
    } finally {
      this.busy = false;
      this.render();
    }
  }

  /** Shopify answers 4xx with {status, message, description} and, for recipient fields, {errors:{email:[…]}}. */
  #showStoreErrors(data) {
    const map = { email: 'email', name: 'name', message: 'message', send_on: 'send_on' };
    let shownField = false;
    if (data && data.errors && typeof data.errors === 'object') {
      for (const [k, v] of Object.entries(data.errors)) {
        if (!map[k]) continue;
        this.#fieldError(map[k], `${Array.isArray(v) ? v.join(', ') : v}.`);
        shownField = true;
      }
    }
    const text = typeof data?.description === 'string' ? data.description
      : typeof data?.message === 'string' && data.message !== 'Cart Error' ? data.message : '';
    this.error(shownField ? (text || 'Please check the recipient details above.') : `Couldn’t add this to your cart: ${text || 'please try again'}.`);
  }

  #fieldError(key, text) {
    const el = this.querySelector(`[data-agc-error="${key}"]`);
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    const input = { email: this.refs.recipientEmail, name: this.refs.recipientName, message: this.refs.recipientMessage, send_on: this.refs.recipientSendOn }[key];
    input?.setAttribute('aria-invalid', 'true');
  }

  #clearFieldErrors() {
    for (const el of this.querySelectorAll('[data-agc-error]')) { el.hidden = true; el.textContent = ''; }
    for (const f of this.#fields()) f.removeAttribute('aria-invalid');
  }

  error(text) {
    const el = this.refs.error;
    if (!el) return;
    el.hidden = !text;
    el.textContent = text || '';
  }

  /* ─── sticky bar (mobile) ─── */
  setupSticky(root) {
    this.#sticky = root?.querySelector('[data-agc-sticky]') || null;
    if (!this.#sticky || !this.refs.addBtn) return;
    this.#sticky.querySelector('[data-agc-sticky-btn]')?.addEventListener('click', () => this.addToCart(), { signal: this.#ac.signal });
    let atcVisible = true;
    let drawerOpen = false;
    const update = () => this.setStickyVisible(!atcVisible && !drawerOpen && window.innerWidth < 1024);
    const measure = () => { atcVisible = this.refs.addBtn.getBoundingClientRect().bottom > 0; };
    this.#stickyIO = new IntersectionObserver(() => { measure(); update(); }, { threshold: 0 });
    this.#stickyIO.observe(this.refs.addBtn);
    window.addEventListener('scroll', () => { measure(); update(); }, { passive: true, signal: this.#ac.signal });
    const dialog = document.querySelector('aromely-cart-drawer-component dialog, cart-drawer-component dialog');
    if (dialog) {
      drawerOpen = dialog.open;
      this.#drawerMO = new MutationObserver(() => { drawerOpen = dialog.open; update(); });
      this.#drawerMO.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
    window.addEventListener('resize', update, { signal: this.#ac.signal });
  }

  setStickyVisible(show) {
    if (!this.#sticky) return;
    this.#sticky.hidden = !show;
    const root = this.closest('[data-agc-root]');
    if (root) root.style.setProperty('--sticky-h', show ? `${this.#sticky.offsetHeight}px` : '0px');
  }

  /* ─── paint ─── */
  #paintPreview() {
    const r = this.refs;
    const a = this.#amount;
    const cardAmount = this.#outer('cardAmount');
    const cardTo = this.#outer('cardTo');
    const cardMsg = this.#outer('cardMsg');
    if (cardAmount && a) cardAmount.textContent = this.money(a.price);
    const name = this.mode === 'recipient' ? (r.recipientName?.value || '').trim() : '';
    if (cardTo) cardTo.textContent = name ? `For ${name}` : (this.mode === 'recipient' ? 'For someone you love' : 'For you');
    const msg = this.mode === 'recipient' ? (r.recipientMessage?.value || '').trim() : '';
    if (cardMsg) { cardMsg.textContent = msg; cardMsg.hidden = !msg; }
    if (r.charCount && r.recipientMessage) r.charCount.textContent = `${r.recipientMessage.value.length}/200`;
  }

  render() {
    const r = this.refs;
    const a = this.#amount;
    const recipient = this.mode === 'recipient';
    for (const b of r.amountBtns || []) b.setAttribute('aria-pressed', String(a && String(a.id) === b.dataset.amountId));
    const price = this.#outer('price');
    if (price && a) price.textContent = this.money(a.price);
    if (r.guide) {
      const g = a?.guide || '';
      r.guide.textContent = g;
      r.guide.hidden = !g;
    }
    if (r.segSelf) r.segSelf.setAttribute('aria-pressed', String(!recipient));
    if (r.segOther) r.segOther.setAttribute('aria-pressed', String(recipient));
    if (r.fields) r.fields.hidden = !recipient;
    for (const f of this.#fields()) f.disabled = !recipient;
    if (r.qtyWrap) r.qtyWrap.hidden = recipient;
    if (r.qtyValue) r.qtyValue.value = String(this.qty);
    if (r.qtyMinus) r.qtyMinus.disabled = this.qty <= 1;
    if (r.qtyPlus) r.qtyPlus.disabled = this.qty >= 10;
    const available = !!a?.available;
    const total = (a?.price || 0) * this.qty;
    if (r.addBtn) {
      r.addBtn.disabled = !available || this.busy;
      r.addBtn.dataset.state = this.busy ? 'loading' : '';
      if (r.addLabel) r.addLabel.textContent = !a ? 'Choose an amount' : !available ? 'Unavailable' : `Add to cart · ${this.money(total)}`;
    }
    if (this.#sticky) {
      const price = this.#sticky.querySelector('[data-agc-sticky-price]');
      const label = this.#sticky.querySelector('[data-agc-sticky-label]');
      const btn = this.#sticky.querySelector('[data-agc-sticky-btn]');
      if (price) price.textContent = this.money(total);
      if (label) label.textContent = recipient ? 'eGift Card · sent to them by email' : `eGift Card${this.qty > 1 ? ` × ${this.qty}` : ''}`;
      if (btn) { btn.textContent = available ? 'Add to cart' : 'Unavailable'; btn.disabled = !available || this.busy; }
    }
    this.#paintPreview();
  }
}

if (!customElements.get('aromely-giftcard-pdp')) customElements.define('aromely-giftcard-pdp', AromelyGiftcardPdp);
